import { Country } from '@tiendanube/common/src/enums';
import { NUVEMPAGO_ENABLED } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useRoles } from 'domains/Auth/authSlice/useRoles';
import { useGetCountry, useIsLogged } from 'domains/Auth/hooks';
import { useNuvemPagoIsActive } from 'domains/Payments/PaymentProviders/hooks';
import { useNuvemPagoFeatureFlagQuery } from '../nuvemPagoCommonsApi';

const AVAILABLE_NUVEMPAGO_COUNTRIES = [Country.BR, Country.AR, Country.MX];
const DEFAULT_CACHE_TTL = 120; // 2 minutes

interface QueryOptions {
  skip?: boolean;
  refetchOnMountOrArgChange?: boolean | number;
  pollingInterval?: number;
  refetchOnReconnect?: boolean;
  refetchOnFocus?: boolean;
}

export function useNuvemPagoFeatureFlag(
  featureFlag: string,
  options: QueryOptions = {},
) {
  const storeCountry = useGetCountry();
  const { isLogin } = useIsLogged();
  const { validateRoles } = useRoles();
  const [hasNuvempagoEnabled] = useHasTags(NUVEMPAGO_ENABLED);
  const { nuvemPagoIsActive } = useNuvemPagoIsActive();

  const hasFullRole = validateRoles(['full', 'payments']);
  const availableNuvempago =
    AVAILABLE_NUVEMPAGO_COUNTRIES.includes(storeCountry);
  const shouldHasNuvempagoTag =
    storeCountry === Country.BR ? true : hasNuvempagoEnabled;

  const requireFetch =
    isLogin &&
    shouldHasNuvempagoTag &&
    availableNuvempago &&
    hasFullRole &&
    !nuvemPagoIsActive;

  const queryResult = useNuvemPagoFeatureFlagQuery(featureFlag, {
    ...options,
    skip: options.skip ?? !requireFetch,
    refetchOnMountOrArgChange:
      options.refetchOnMountOrArgChange ?? DEFAULT_CACHE_TTL,
  });

  const isFeatureEnabled = queryResult.isSuccess
    ? (queryResult.data as boolean)
    : queryResult.isError
    ? false
    : null;

  return {
    ...queryResult,
    featureFlag,
    isFeatureEnabled,
  };
}
